import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { UserPlus, Search, Edit2, Trash2, X, CheckCircle, Briefcase } from 'lucide-react';

const PageContainer = styled.div`
  padding: 2rem;
  max-width: 900px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: 1.5rem;

  h1 { font-size: 1.875rem; font-weight: 700; color: #1e293b; margin: 0 0 0.35rem 0; }
  p { color: #64748b; margin: 0; }
`;

const TopBar = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;

const SearchBar = styled.div`
  position: relative;
  flex: 1;
  min-width: 200px;

  svg { position: absolute; left: 1rem; top: 50%; transform: translateY(-50%); color: #9ca3af; }

  input {
    width: 100%;
    padding: 0.75rem 1rem 0.75rem 2.75rem;
    border: 2px solid #e5e7eb;
    border-radius: 12px;
    font-size: 1rem;
    box-sizing: border-box;
    &:focus { outline: none; border-color: #06b6d4; }
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%);
  color: white;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  white-space: nowrap;

  &:hover { background: #0891b2; }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: white;
  border: 2px solid #f1f5f9;
  border-radius: 14px;
  padding: 0.9rem 1.1rem;
  transition: border-color 0.2s;

  &:hover { border-color: #06b6d4; }
`;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #06b6d4, #0891b2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
`;

const Name = styled.div`
  flex: 1;
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
`;

const IconBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.875rem;
  border: none;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;

  &.edit { background: #eff6ff; color: #2563eb; &:hover { background: #dbeafe; } }
  &.delete { background: #fef2f2; color: #dc2626; &:hover { background: #fee2e2; } }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #94a3b8;
  svg { margin-bottom: 1rem; opacity: 0.4; }
  h3 { font-size: 1.25rem; font-weight: 600; color: #64748b; margin: 0 0 0.5rem 0; }
  p { margin: 0; }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1100;
  backdrop-filter: blur(4px);
`;

const ModalContent = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 20px;
  width: 90%;
  max-width: 440px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
`;

const ModalTitle = styled.h2`
  color: #1e293b;
  font-size: 1.3rem;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CloseBtn = styled.button`
  background: none; border: none; cursor: pointer; padding: 4px; color: #64748b;
  &:hover { color: #1e293b; }
`;

const Label = styled.label`
  display: block; font-size: 0.875rem; font-weight: 600; color: #374151; margin-bottom: 0.4rem;
`;

const InputField = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 1rem;
  box-sizing: border-box;
  &:focus { outline: none; border-color: #06b6d4; }
`;

const ModalActions = styled.div`
  display: flex; gap: 0.75rem; margin-top: 1.5rem;
`;

const ModalBtn = styled.button`
  flex: 1;
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;

  &.cancel { background: #f1f5f9; color: #475569; &:hover { background: #e2e8f0; } }
  &.save { background: #06b6d4; color: white; &:hover { background: #0891b2; } &:disabled { opacity: 0.5; cursor: not-allowed; } }
  &.danger { background: #dc2626; color: white; &:hover { background: #b91c1c; } }
`;

const SuccessMsg = styled.div`
  background: #d1fae5;
  border: 1px solid #a7f3d0;
  color: #065f46;
  padding: 0.75rem 1rem;
  border-radius: 12px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  font-size: 0.875rem;
`;

const OccupationsPage = () => {
  const { t } = useTranslation();
  const [occupations, setOccupations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const fetchOccupations = async () => {
    try {
      const res = await axios.get('/api/occupations', authHeaders());
      if (res.data.success) setOccupations(res.data.occupations || []);
    } catch (e) {
      console.error('Error fetching occupations:', e);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchOccupations(); }, []);

  const openAdd = () => { setEditing(null); setName(''); setSuccessMsg(''); setShowModal(true); };
  const openEdit = (occ) => { setEditing(occ); setName(occ.name); setSuccessMsg(''); setShowModal(true); };

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      if (editing) {
        const res = await axios.patch(`/api/occupations/${editing._id}`, { name: trimmed }, authHeaders());
        if (res.data.success) setSuccessMsg(t('occupations.updated'));
      } else {
        const res = await axios.post('/api/occupations', { name: trimmed }, authHeaders());
        if (res.data.success) setSuccessMsg(t('occupations.added'));
      }
      await fetchOccupations();
      setTimeout(() => { setShowModal(false); setSuccessMsg(''); }, 900);
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to save occupation');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/occupations/${id}`, authHeaders());
      setConfirmDelete(null);
      await fetchOccupations();
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to delete occupation');
    }
  };

  const filtered = occupations.filter(o => o.name.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <PageContainer>
      <Header>
        <h1>{t('occupations.title')}</h1>
        <p>{t('occupations.subtitle')}</p>
      </Header>

      <TopBar>
        <SearchBar>
          <Search size={18} />
          <input
            type="text"
            placeholder={t('occupations.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchBar>
        <AddButton onClick={openAdd}>
          <UserPlus size={18} />
          {t('occupations.add')}
        </AddButton>
      </TopBar>

      {loading ? (
        <EmptyState><p>Loading…</p></EmptyState>
      ) : filtered.length === 0 ? (
        <EmptyState>
          <Briefcase size={48} />
          <h3>{searchTerm ? t('occupations.noMatch') : t('occupations.none')}</h3>
          <p>{searchTerm ? 'Try a different search term' : t('occupations.addHint')}</p>
        </EmptyState>
      ) : (
        <List>
          {filtered.map(occ => (
            <Row key={occ._id}>
              <Avatar><Briefcase size={18} /></Avatar>
              <Name>{occ.name}</Name>
              <IconBtn className="edit" onClick={() => openEdit(occ)}>
                <Edit2 size={14} /> {t('occupations.edit')}
              </IconBtn>
              <IconBtn className="delete" onClick={() => setConfirmDelete(occ._id)}>
                <Trash2 size={14} /> {t('occupations.remove')}
              </IconBtn>
              {confirmDelete === occ._id && (
                <div style={{ position: 'absolute' }} />
              )}
            </Row>
          ))}
        </List>
      )}

      {confirmDelete && (
        <ModalOverlay onClick={() => setConfirmDelete(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle style={{ marginBottom: '1rem' }}>{t('occupations.removeTitle')}</ModalTitle>
            <p style={{ color: '#475569', marginTop: 0 }}>
              {t('occupations.removeConfirm', { name: occupations.find(o => o._id === confirmDelete)?.name || '' })}
            </p>
            <ModalActions>
              <ModalBtn className="cancel" onClick={() => setConfirmDelete(null)}>{t('occupations.cancel')}</ModalBtn>
              <ModalBtn className="danger" onClick={() => handleDelete(confirmDelete)}>{t('occupations.remove')}</ModalBtn>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}

      {showModal && (
        <ModalOverlay onClick={() => { setShowModal(false); setSuccessMsg(''); }}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                <UserPlus size={22} color="#06b6d4" />
                {editing ? t('occupations.editTitle') : t('occupations.addTitle')}
              </ModalTitle>
              <CloseBtn onClick={() => { setShowModal(false); setSuccessMsg(''); }}>
                <X size={22} />
              </CloseBtn>
            </ModalHeader>

            {successMsg && (
              <SuccessMsg><CheckCircle size={16} />{successMsg}</SuccessMsg>
            )}

            <Label>{t('occupations.name')} *</Label>
            <InputField
              type="text"
              placeholder={t('occupations.namePlaceholder')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSave(); } }}
              autoFocus
            />

            <ModalActions>
              <ModalBtn className="cancel" onClick={() => { setShowModal(false); setSuccessMsg(''); }}>
                {t('occupations.cancel')}
              </ModalBtn>
              <ModalBtn className="save" onClick={handleSave} disabled={!name.trim() || saving}>
                {saving ? '…' : editing ? t('occupations.update') : t('occupations.save')}
              </ModalBtn>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
    </PageContainer>
  );
};

export default OccupationsPage;
